import React, { useLayoutEffect, useRef, useState } from 'react'
import { Tooltip } from '@mui/material'
import { CalendarMonthOutlined, TextSnippetOutlined } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { bucketRailClass, statusDisplay, formatClock } from './byGroupVisuals'
import { systemValueCodeLabel, systemValueLabel } from '../generic/systemValues/systemValueLabel'
import DeviceHeroImage from './DeviceHeroImage'
import PreparationLabel from '../Mos/Widgets/common/PreparationLabel'
import MachineResourcesList from './MachineResourcesList'
import MachineAlarmBadge from './MachineAlarmBadge'
import AssetResourcesButton from './AssetResourcesButton'
import AssetUnlockButton from './AssetUnlockButton'

// NOTE — deliberate duplication (PO directive, story 35-monitoring-screen-redesign):
// independent presentation of the by-group grid card, transcribed from the Stitch
// mockups. Only the DATA/LOGIC layer is shared with /monitoring (`useAssetLiveData`,
// `AssetCardDialogs`, `plantMonitoringUtils`); `AssetCardView.jsx` stays untouched.

const BUCKET_CARD_MODIFIER = {
  WORKING: 'mcx-card--working',
  ERROR: 'mcx-card--error',
  IDLE: 'mcx-card--idle',
  NON_OPERATIVE: 'mcx-card--nonop'
}

/** Single operative touch button (icon + label). */
/**
 * Historia 140 (MIC-4182) — what the machine IS, next to what it is called.
 *
 * The operator reads a code on the card and has to translate it from memory. The description is
 * already maintained on the device configuration screen and already travels in the Monitoring
 * snapshot, so this only renders it. No request, no new field: the whole path from the snapshot to
 * here is a spread, not an allow-list.
 *
 * ⚠️ **A machine with no description is the NORMAL case, not an error.** Absent, `null`, empty or
 * only spaces: nothing is rendered at all. Not an empty line, not a lone label, not an em-dash of
 * filler -- the card keeps exactly the elements it had before this story. That is why the emptiness
 * check is on the trimmed value while what gets painted is the value as it arrived.
 *
 * ⚠️⚠️ **This component does not compare the description with ANYTHING, and that is the contract.**
 * If a plant writes a description identical to the machine's name or type, it is shown twice, and
 * that is what the product owner asked for (decision of 2026-09-15, verbatim: "se ve dos veces").
 * There is a tempting precedent in this very module pulling the other way --
 * `sensorAlarmUtils.getDisplayDescription` DOES hide a description that repeats the error code --
 * and copying it here breaks the story. The criterion is written in the feature file: any comparison
 * of the description against the name or the type means this is broken, even with the tests green.
 *
 * ⚠️ **One line, and the rest in a tooltip** (decision of Iosu, 2026-09-15). What decides where the
 * text is cut is the REAL WIDTH of the card, never a character count: the font is proportional and
 * the two densities are different widths, so any fixed number would cut too early in one and leave
 * a gap in the other. The tooltip is offered only when the text is genuinely clipped, which is what
 * `scrollWidth > clientWidth` answers -- so a description that fits carries no tooltip at all.
 *
 * ⚠️ **Known limit of that decision, written here rather than argued away:** Monitoring is a
 * shop-floor screen and **a touch panel has no hover**. Where this card is read on a touch screen
 * the tooltip does not exist, and a long description is cut with no way to read the rest. It is
 * built as asked -- the decision is the product owner's -- and the standing design criterion for
 * this repo ("nothing may depend on hover to work, the operator wears gloves") pulls the other way.
 * Raised for the gate; not patched here on my own initiative.
 *
 * @param {Object} props Props object
 * @param {Object} props.device Device node of the Monitoring snapshot
 * @param {string} props.className Class of the density that is rendering it
 * @returns {JSX.Element|null} The description, or nothing when there is none
 */
function AssetDescription ({ device, className }) {
  const textRef = useRef(null)
  const [clipped, setClipped] = useState(false)
  const description = device?.description
  const hasDescription = typeof description === 'string' && description.trim() !== ''

  // Measured after layout and re-measured when the card is resized: the same description is clipped
  // in a narrow column and whole in a wide one, so asking once on mount would be right only until
  // the grid reflows.
  useLayoutEffect(() => {
    const node = textRef.current
    if (!hasDescription || node === null) {
      return
    }
    const measure = () => setClipped(node.scrollWidth > node.clientWidth)
    measure()
    if (typeof ResizeObserver !== 'function') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasDescription, description])

  if (!hasDescription) {
    return null
  }
  return (
    <span
      ref={textRef}
      className={className}
      data-testid={`asset-card-description-${device.id}`}
      title={clipped ? description : undefined}
    >
      {description}
    </span>
  )
}

function TouchAction ({ testId, icon: Icon, label, disabled, onClick }) {
  return (
    <Tooltip title={label}>
      <span style={{ minWidth: 0, display: 'flex', flex: 1 }}>
        <button type='button' data-testid={testId} className='mcx-touch-btn' style={{ flex: 1 }} disabled={disabled} onClick={onClick}>
          <Icon sx={{ fontSize: 22 }} />
          <span className='mcx-touch-btn__label'>{label}</span>
        </button>
      </span>
    </Tooltip>
  )
}

/**
 * Card action bar (PO): the touch-button bar as before, but "Physical parts" and
 * "Manage location" are unified into one general "Resources" button; Documents and
 * Calendar stay as their own buttons. Shared by both card modes.
 */
function ActionBar ({ actions, deviceId, t_, hasResources = true }) {
  return (
    <div className='mcx-card__actions'>
      <AssetResourcesButton actions={actions} deviceId={deviceId} hasResources={hasResources} />
      <TouchAction
        testId={`asset-card-action-documents-${deviceId}`}
        icon={TextSnippetOutlined}
        label={t_('Documents')}
        disabled={!actions.isUserAuthorized('DOCUMENTS_DEVICE')}
        onClick={() => actions.openDocument()}
      />
      <TouchAction
        testId={`asset-card-action-calendar-${deviceId}`}
        icon={CalendarMonthOutlined}
        label={t_('Calendar')}
        disabled={!actions.isUserAuthorized('CALENDARS')}
        onClick={() => actions.goToCalendar()}
      />
    </div>
  )
}

/**
 * AssetCardByGroupView — the by-group grid card, in two densities (PO):
 *
 * - `compact` (default): the Stitch "showroom" card — full-card photo, floating
 *   status/mode chips, name/type + part chip, PROGRAM/TOOL rows and the action bar.
 * - `production`: a data-dense card (no photo hero) — header row (thumbnail, name,
 *   type, status pill, mode), a KPI tile row (OEE/CYCLE/PARTS TODAY — placeholders
 *   until sun ships them), ACTIVE PROGRAM / CURRENT TOOL, a PARTS ON MACHINE list
 *   (code + storage position + lock), an incidence footer, and the action bar.
 *
 * Both share the same data (`deviceInfo`, `locations`, `device`) and the same
 * action bar (`ActionBar`): the touch buttons as before, but "Physical parts" +
 * "Manage location" unified into one "Resources" button (PO). Status colour comes
 * from `statusDisplay` (sun's bucket when present, else the real raw status — RN3).
 */
export default function AssetCardByGroupView ({ device, deviceInfo, actions, onOpenDetail = () => {}, locations = [], mode = 'compact', production = {}, mainImageInfo = {} }) {
  const { t_ } = useSmartTranslation()
  const display = statusDisplay(device)
  const bucket = display.bucket
  const pulsing = bucket === 'WORKING' || bucket === 'ERROR'
  const statusText = display.statusCode === '' ? '—' : systemValueLabel(t_, display.statusDomain, display.statusCode)
  // Story 204 — S76 (DL17): the device type is an enum, shown by its code.
  const deviceTypeLabel = systemValueLabel(t_, 'DeviceTypeEnum', device?.deviceType?.name ?? '')

  const cardClass = [
    mode === 'production' ? 'mcx-prodcard' : 'mcx-card',
    bucketRailClass(bucket),
    BUCKET_CARD_MODIFIER[bucket] ?? ''
  ].filter(Boolean).join(' ')

  if (mode === 'production') {
    const incidences = device.incidenceQuantity ?? 0
    // A HANDLER (robot/loader) has no OF/operation of its own; its card shows what it is
    // CARRYING and the movement in course, from `production.moves` (story 35, PO).
    const isHandler = device?.deviceType?.name === 'HANDLER' // system-value: raw-ok
    const moves = production?.moves ?? []
    // Real production data (story 35, monitoring/deviceProduction): active-order summary
    // (OF + part reference) plus the live work (active part + status + start time).
    // `order`/`work` are {} when sun omits them (no active OF / no operation).
    const order = production?.order ?? {}
    const work = production?.work ?? {}
    const hasOrder = Object.keys(order).length > 0
    // Card (small format): show ONLY the fabrication order code (PO).
    const orderSummary = order.fabricationOrderCode ?? ''
    const activePart = work.partCode ?? ''
    const partStatus = work.partStatus ?? ''
    const startedAt = formatClock(work.jobStartedAtMillis || work.taskStartedAtMillis)
    // Free-form driver telemetry (Map<String,String> filled per machine driver); shown
    // as compact key/value rows on the card, only when it carries data.
    const driverEntries = Object.entries(work.driverInfo ?? {})

    return (
      <article className={cardClass} data-bucket={bucket} data-testid={`asset-card-by-group-${device.id}`}>
        {/* The whole header bar opens the production fullscreen detail (PO: the
            detail lives in production mode; tapping anywhere on the bar works). It
            is a clickable div (not a <button>) so the NON_OPERATIVE unlock button
            can live inside it without nesting interactive elements. */}
        <div
          role='button'
          tabIndex={0}
          data-testid={`asset-card-open-detail-${device.id}`}
          className='mcx-prodcard__head'
          aria-label={t_('Asset detail')}
          onClick={() => onOpenDetail(device)}
          onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpenDetail(device) } }}
        >
          <span className='mcx-prodcard__id'>
            <span className='mcx-prodcard__thumb'><DeviceHeroImage device={device} mainImageInfo={mainImageInfo} /></span>
            <span style={{ minWidth: 0 }}>
              <span className='mcx-prodcard__name'>{device.name}</span>
              {device?.deviceType?.name && <span className='mcx-prodcard__type'>{deviceTypeLabel}</span> /* system-value: raw-ok */}
              <AssetDescription device={device} className='mcx-prodcard__description' />
            </span>
            {/* S152 (A1/C6): the station's mark travels in the SNAPSHOT, not in the production
                block, so the very same label renders in both densities without an extra request.
                The front never recomputes RN28.1d -- it shows the value that arrives (S154). */}
            <PreparationLabel visible={device?.jobInPreparation === true} />
          </span>
          {/* Status + mode grouped into one cluster (PO), with the quick unlock
              action when the machine is NON_OPERATIVE. */}
          <span className='mcx-prodcard__statusgroup'>
            <MachineAlarmBadge summary={device.alarmSummary} testId={`asset-card-alarm-badge-${device.id}`} />
            <span className={`mcx-status-chip mcx-status-chip--${bucket}${pulsing ? ' mcx-pulse' : ''}`}>{statusText}</span>
            <span className='mcx-prodcard__mode'>
              <span className='mcx-label-caps--xxs mcx-label-caps' style={{ fontSize: 9, color: 'var(--mcx-on-variant)' }}>{t_('Mode')}</span>
              <span className='mcx-prodcard__mode-value'>{device.mode ? systemValueLabel(t_, 'DeviceModeEnum', device.mode) : '—'}</span>
            </span>
            <AssetUnlockButton device={device} />
          </span>
        </div>

        {isHandler
          ? (
            /* HANDLER card: what it is carrying + the movement in course (from moves[]).
               `--fill` makes this panel grow so the card matches sibling heights and the
               action bar sits at the bottom (like `.mcx-prodparts` does on normal cards).
               No severity rail here: full-height it reads as a long misaligned line. */
            <div className='mcx-prodpanel mcx-prodpanel--fill' data-testid={`asset-card-moves-${device.id}`}>
              {moves.length === 0
                ? <span className='mcx-prodcard__order-empty'>{t_('No movement in progress')}</span>
                : moves.map((move, index) => (
                  <div key={index} className='mcx-cardmove'>
                    <div className='mcx-datarow'>
                      <span className='mcx-datarow__label'>{t_('Carrying')}</span>
                      <span className={`mcx-datarow__value${move.fixtureCode ? ' mcx-datarow__value--accent' : ' mcx-datarow__value--empty'}`}>
                        {move.fixtureCode || '—'}
                      </span>
                    </div>
                    <div className='mcx-datarow'>
                      <span className='mcx-datarow__label'>{systemValueCodeLabel(t_, 'RunningMove.Direction', move.direction) || t_('Route')}</span>
                      <span className='mcx-datarow__value'>
                        {[move.fromDeviceName, move.toDeviceName].filter(Boolean).join(' → ') || '—'}
                      </span>
                    </div>
                    {move.startedAtMillis
                      ? (
                        <div className='mcx-datarow'>
                          <span className='mcx-datarow__label'>{t_('Started')}</span>
                          <span className='mcx-datarow__value'>{formatClock(move.startedAtMillis)}</span>
                        </div>
                        )
                      : null}
                    {Object.entries(move.driverInfo ?? {}).map(([key, value]) => (
                      <div className='mcx-datarow' key={key}>
                        <span className='mcx-datarow__label'>{key}</span>
                        <span className='mcx-datarow__value'>{String(value ?? '—')}</span>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
            )
          : (
            <>
              {/* Active-order summary (real data, monitoring/deviceProduction) */}
              <div className='mcx-prodcard__order' data-testid={`asset-card-order-${device.id}`}>
                {hasOrder
                  ? <span className='mcx-prodcard__order-code'>{orderSummary || '—'}</span>
                  : <span className='mcx-prodcard__order-empty'>{t_('No active fabrication order')}</span>}
              </div>

              {/* Live work: active part (+ status) and start time (real data) */}
              <div className={`mcx-prodpanel ${bucketRailClass(bucket)}`} data-testid={`asset-card-work-${device.id}`}>
                <div className='mcx-datarow'>
                  <span className='mcx-datarow__label'>{t_('Active part')}</span>
                  <span
                    className={`mcx-datarow__value${activePart ? ' mcx-datarow__value--accent' : ' mcx-datarow__value--empty'}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{activePart || '—'}</span>
                    {partStatus && <span className='mcx-chip mcx-chip--sm'>{systemValueCodeLabel(t_, 'PartStatusEnum', partStatus)}</span>}
                  </span>
                </div>
                <div className='mcx-datarow'>
                  <span className='mcx-datarow__label'>{t_('Started')}</span>
                  <span className={`mcx-datarow__value${startedAt ? '' : ' mcx-datarow__value--empty'}`}>
                    {startedAt || '—'}
                  </span>
                </div>
                {/* Free-form driver telemetry: key = label, value next (only when populated) */}
                {driverEntries.map(([key, value]) => (
                  <div className='mcx-datarow' key={key} data-testid={`asset-card-driver-${device.id}-${key}`}>
                    <span className='mcx-datarow__label'>{key}</span>
                    <span className='mcx-datarow__value'>{String(value ?? '—')}</span>
                  </div>
                ))}
              </div>

              {/* Resources on machine — frontend-paginated (a storage can hold 100+) */}
              <MachineResourcesList locations={locations} testIdPrefix={`asset-part-line-${device.id}`} pageSize={4} />
            </>
            )}

        {/* Footer: incidence badge */}
        {incidences > 0 && (
          <div className='mcx-prodcard__footer'>
            <span className='mcx-incidence-badge' data-testid={`asset-incidence-${device.id}`}>
              {incidences} {incidences === 1 ? t_('Incidence') : t_('Incidences')}
            </span>
          </div>
        )}

        <ActionBar actions={actions} deviceId={device.id} t_={t_} hasResources={locations.length > 0} />
      </article>
    )
  }

  // ---- compact (default) ----
  const partCount = locations.length
  const partCode = locations[0]?.resources?.[0]?.code ?? ''

  return (
    <article className={cardClass} data-bucket={bucket}>
      {/* Full-card photo background with a top-to-bottom gradient for text legibility. */}
      <div className='mcx-card__photo' aria-hidden='true'>
        <DeviceHeroImage device={device} mainImageInfo={mainImageInfo} />
        <span className='mcx-card__photo-fade' />
      </div>

      {/* Clicking anywhere on the card (except the unlock button — it stopsPropagation —
          and the action bar, which sits outside this region) opens the production
          fullscreen detail, where the alarms panel lives (PO). Clickable div, not a
          <button>, so the unlock <button> can nest inside without invalid nesting. */}
      <div
        role='button'
        tabIndex={0}
        data-testid={`asset-card-open-detail-${device.id}`}
        className='mcx-card__clickable'
        aria-label={t_('Asset detail')}
        onClick={() => onOpenDetail(device)}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpenDetail(device) } }}
      >
        <div className='mcx-card__hero' data-testid={`asset-card-by-group-${device.id}`}>
          <div className='mcx-card__hero-topleft'>
            <span className={`mcx-status-chip mcx-status-chip--${bucket}${pulsing ? ' mcx-pulse' : ''}`}>{statusText}</span>
            {/* S152/S156: from the snapshot, so the compact density -- which does not request the
                production block -- shows it too, with no extra request per tick. */}
            <PreparationLabel visible={device?.jobInPreparation === true} />
            <MachineAlarmBadge summary={device.alarmSummary} testId={`asset-card-alarm-badge-${device.id}`} />
          </div>

          <div className='mcx-card__hero-topright'>
            <span className='mcx-mode-chip'>
              <span className='mcx-label-caps--xxs mcx-label-caps' style={{ fontSize: 9 }}>{t_('Mode')}</span>
              <span className='mcx-mode-chip__value'>{device.mode ? systemValueLabel(t_, 'DeviceModeEnum', device.mode) : '—'}</span>
            </span>
            <AssetUnlockButton device={device} />
          </div>
        </div>

        <div className='mcx-card__body'>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <h3 className='mcx-card__name'>{device.name}</h3>
              <p className='mcx-card__type'>{deviceTypeLabel}</p>
              <AssetDescription device={device} className='mcx-card__description' />
            </div>
            {partCount > 0 && (
              <span className='mcx-part-chip mcx-part-chip--accent' data-testid={`asset-card-part-${device.id}`}>
                <span>{partCount > 1 ? `${partCount} ${t_('Resources')}` : partCode}</span>
              </span>
            )}
          </div>

          {/* Program/Tool only when there is data — otherwise hide so the photo shows more (PO) */}
          {(deviceInfo?.programName || deviceInfo?.toolName) && (
            <div>
              {deviceInfo?.programName && (
                <div className='mcx-datarow'>
                  <span className='mcx-datarow__label'>{t_('Program')}</span>
                  <span className='mcx-datarow__value mcx-datarow__value--accent'>{deviceInfo.programName}</span>
                </div>
              )}
              {deviceInfo?.toolName && (
                <div className='mcx-datarow'>
                  <span className='mcx-datarow__label'>{t_('Tool')}</span>
                  <span className='mcx-datarow__value'>{deviceInfo.toolName}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ActionBar actions={actions} deviceId={device.id} t_={t_} hasResources={locations.length > 0} />
    </article>
  )
}
